/**
 * App Routes Configuration
 */

import { lazy, Suspense, useEffect } from "react"
import { createBrowserRouter, Navigate, useNavigate, useSearchParams } from "react-router-dom"
import { ResponsiveAuthRoute } from "@/components/mobile/ResponsiveAuthRoute"
import { ForgotPasswordPage } from "@/features/auth/pages/forgotPasswordPage"
import { LoginScreen } from "@/features/auth/pages/loginPage"
import {
  MobileForgotPasswordPage,
  MobileLoginScreen,
  MobilePermissionSetupScreen,
  MobileResetPasswordPage,
  MobileSignupScreen,
  MobileVerifyEmailScreen,
  MobileWelcomeScreen,
} from "@/features/auth/pages/mobile"
import { ResetPasswordPage } from "@/features/auth/pages/resetPasswordPage"
import { SignupScreen } from "@/features/auth/pages/signupPage"
import { EmailVerificationScreen } from "@/features/auth/pages/verifyEmail"

const ExpertsCatalogScreen = lazy(() =>
  import("@/features/catalog/pages/experts").then(m => ({ default: m.ExpertsCatalogScreen })),
)
const RitualistsCatalogScreen = lazy(() =>
  import("@/features/catalog/pages/ritualists").then(m => ({ default: m.RitualistsCatalogScreen })),
)
const MembersCatalogScreen = lazy(() =>
  import("@/features/catalog/pages/members").then(m => ({ default: m.MembersCatalogScreen })),
)
const IntentionsCatalogScreen = lazy(() =>
  import("@/features/catalog/pages/intentions").then(m => ({ default: m.IntentionsCatalogScreen })),
)
const SearchPageScreen = lazy(() =>
  import("@/features/search/pages/SearchPage").then(m => ({ default: m.SearchPage })),
)

import { ChatLayout } from "@/features/chats/components/ChatLayout"
import { AiChatArea } from "@/features/chats/components/chat/AiChatArea"
import { DmChatArea } from "@/features/chats/components/dm/DmChatArea"
import { ChatLandingPage } from "@/features/chats/components/landing/ChatLandingPage"
import { RitualGroupChatArea } from "@/features/chats/components/ritual/RitualGroupChatArea"
import { CommunityDetailScreen } from "@/features/communities/pages/communityDetail"
import { CreateCommunityScreen } from "@/features/communities/pages/createCommunity"
import { EditCommunityScreen } from "@/features/communities/pages/editCommunity"
import { ConnectScreen } from "@/features/connect/pages/connect"
import { DashboardScreen } from "@/features/dashboard/pages/dashboard"
import { TooManyRequestsPage } from "@/features/error/pages/tooManyRequestsPage"
import { IntentionDetailScreen } from "@/features/intentions/pages/intentionDetail"
import { LearnAndPracticeScreen } from "@/features/learn-practice/pages/learnAndPractice"
import { MyRitualityScreen } from "@/features/my-rituality/pages/myRituality"
import { PermissionSetupScreen } from "@/features/onboarding/pages/setupPermission"
import { WelcomeScreen } from "@/features/onboarding/pages/welcomeUser"
import { EditProfileScreen } from "@/features/profile/pages/editProfile"
import { OtherProfileScreen } from "@/features/profile/pages/otherProfile"
import { ProfileScreen } from "@/features/profile/pages/profile"
import { CreateRitualScreen } from "@/features/rituals/pages/createRitual"
import { EditRitualScreen } from "@/features/rituals/pages/editRitual"

const CreateCoachingScreen = lazy(() =>
  import("@/features/coaching/pages/createCoaching").then(m => ({
    default: m.CreateCoachingScreen,
  })),
)

const EditCoachingScreen = lazy(() =>
  import("@/features/coaching/pages/editCoaching").then(m => ({
    default: m.EditCoachingScreen,
  })),
)

import { RitualDetailScreen } from "@/features/rituals/pages/ritualDetail"
import { SettingsScreen } from "@/features/settings/pages/settings"

const TransactionDetailPage = lazy(() =>
  import("@/features/settings/pages/transactionDetail").then(m => ({
    default: m.TransactionDetailPage,
  })),
)

import { AppLayout } from "@/layout/AppLayout"
import { ProtectedRoute } from "@/routes/ProtectedRoute"

// OAuth callback handler component
function OAuthCallbackHandler() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  useEffect(() => {
    const accessToken = searchParams.get("accessToken")
    const refreshToken = searchParams.get("refreshToken")
    const expiresIn = searchParams.get("expiresIn")
    const profileRequired = searchParams.get("profileRequired")
    const error = searchParams.get("error")
    const deviceId = searchParams.get("deviceId")
    const isGoogle = searchParams.get("google") === "true"
    const isApple = searchParams.get("apple") === "true"
    const googleName = searchParams.get("name")
    const googleEmail = searchParams.get("email")
    const googleUsername = searchParams.get("username")
    const appleName = searchParams.get("name")
    const appleEmail = searchParams.get("email")
    const appleUsername = searchParams.get("username")

    if (error) {
      navigate(`/login?error=${error}`)
      return
    }

    // Handle Google signup flow - new user with pre-filled data
    if (isGoogle && googleName !== null && googleEmail !== null && googleUsername !== null) {
      console.log("[OAuth Callback] Google signup data detected:", {
        googleName,
        googleEmail,
        googleUsername,
      })
      sessionStorage.setItem(
        "google_signup",
        JSON.stringify({
          name: googleName,
          email: googleEmail,
          username: googleUsername,
        }),
      )
      console.log("[OAuth Callback] Redirecting to /signup")
      navigate("/signup")
      return
    }

    // Handle Apple signup flow - new user with pre-filled data
    if (isApple && appleName !== null && appleEmail !== null && appleUsername !== null) {
      sessionStorage.setItem(
        "apple_signup",
        JSON.stringify({
          name: appleName,
          email: appleEmail,
          username: appleUsername,
        }),
      )
      navigate("/signup")
      return
    }

    if (accessToken && refreshToken && expiresIn) {
      // Store tokens in localStorage
      localStorage.setItem("access_token", accessToken)
      localStorage.setItem("refresh_token", refreshToken)
      // Store deviceId if present (for existing users who have a device created)
      if (deviceId) {
        localStorage.setItem("device_id", deviceId)
      }

      // Fetch user profile and set in auth store
      import("@/features/profile/services/user.service")
        .then(({ getProfile }) => {
          getProfile()
            .then(({ user }) => {
              // Import auth store dynamically to avoid circular deps
              import("@/features/auth/store/authStore").then(({ useAuthStore }) => {
                useAuthStore.getState().setUser(user)

                // Redirect based on permissions completion
                if (profileRequired === "true") {
                  navigate("/onboarding/permissions")
                } else {
                  navigate("/")
                }
              })
            })
            .catch(err => {
              console.error("[OAuth Callback] Failed to fetch user profile:", err)
              navigate("/login?error=fetch_user_failed")
            })
        })
        .catch(err => {
          console.error("[OAuth Callback] Failed to load profile service:", err)
          navigate("/login?error=fetch_user_failed")
        })
    } else {
      navigate("/login?error=invalid_callback")
    }
  }, [searchParams, navigate])

  return null
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <DashboardScreen />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/my-rituality",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <MyRitualityScreen />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/profile",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <ProfileScreen />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/learn-and-practice",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <LearnAndPracticeScreen />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/learn-and-practice/intention/:intentionId",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <IntentionDetailScreen />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/connect",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <ConnectScreen />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/connect/experts",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <ExpertsCatalogScreen />
          </Suspense>
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/connect/ritualists",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <RitualistsCatalogScreen />
          </Suspense>
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/connect/members",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <MembersCatalogScreen />
          </Suspense>
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/search",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <SearchPageScreen />
          </Suspense>
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/connect/intentions",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <IntentionsCatalogScreen />
          </Suspense>
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/chats",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <ChatLayout />
        </AppLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <ChatLandingPage />,
      },
      {
        path: "ai/:id",
        element: <AiChatArea />,
      },
      {
        path: "dm/:conversationId",
        element: <DmChatArea />,
      },
      {
        path: "ritual/:ritualGroupConversationId",
        element: <RitualGroupChatArea />,
      },
    ],
  },
  {
    path: "/communities/:id",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <CommunityDetailScreen />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/communities/create",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <CreateCommunityScreen />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/communities/edit/:id",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <EditCommunityScreen />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/coaching-sessions/edit/:id",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <EditCoachingScreen />
          </Suspense>
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/coaching-sessions/create",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <CreateCoachingScreen />
          </Suspense>
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/rituals/create",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <CreateRitualScreen />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/rituals/edit/:id",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <EditRitualScreen />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/rituals/:id",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <RitualDetailScreen />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/profile/:userId",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={false} requireOnboarding={false}>
        <AppLayout>
          <OtherProfileScreen />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/profile/edit",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <EditProfileScreen />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/settings",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <SettingsScreen />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/settings/transactions/:transactionId",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
        <AppLayout>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <TransactionDetailPage />
          </Suspense>
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/welcome",
    element: (
      <ProtectedRoute requireAuth={true} requireOnboarding={true}>
        <ResponsiveAuthRoute
          desktop={WelcomeScreen}
          mobile={MobileWelcomeScreen}
          showBackButton={false}
          showLogo={false}
          centerContent={true}
          enableSmartNavigation={true}
        />
      </ProtectedRoute>
    ),
  },
  {
    path: "/signup",
    element: (
      <ResponsiveAuthRoute desktop={SignupScreen} showLogo={false} mobile={MobileSignupScreen} />
    ),
  },
  {
    path: "/ref/:username",
    element: (
      <ResponsiveAuthRoute desktop={SignupScreen} mobile={MobileSignupScreen} showLogo={false} />
    ),
  },
  {
    path: "/login",
    element: (
      <ResponsiveAuthRoute
        desktop={LoginScreen}
        mobile={MobileLoginScreen}
        showBackButton={false}
      />
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <ResponsiveAuthRoute
        desktop={ForgotPasswordPage}
        mobile={MobileForgotPasswordPage}
        showLogo={false}
        centerContent={true}
      />
    ),
  },
  {
    path: "/reset-password",
    element: (
      <ResponsiveAuthRoute
        desktop={ResetPasswordPage}
        mobile={MobileResetPasswordPage}
        showLogo={false}
        centerContent={true}
      />
    ),
  },
  {
    path: "/verify-email",
    element: (
      <ProtectedRoute requireAuth={true}>
        <ResponsiveAuthRoute
          desktop={EmailVerificationScreen}
          mobile={MobileVerifyEmailScreen}
          showBackButton={true}
          showLogo={false}
          enableSmartNavigation={true}
          onNavigateBack={() => {
            window.location.href = "/login"
          }}
        />
      </ProtectedRoute>
    ),
  },
  {
    path: "/onboarding/permissions",
    element: (
      <ProtectedRoute requireAuth={true} requireVerified={true}>
        <ResponsiveAuthRoute
          desktop={PermissionSetupScreen}
          mobile={MobilePermissionSetupScreen}
          showBackButton={false}
          showLogo={false}
          enableSmartNavigation={true}
        />
      </ProtectedRoute>
    ),
  },
  {
    path: "/error/too-many-requests",
    element: <TooManyRequestsPage />,
  },
  {
    path: "/oauth/callback",
    element: <OAuthCallbackHandler />,
  },
  {
    path: "*",
    element: <Navigate to="/login" replace />,
  },
])
